/**
 * The second consent: Google, on top of the Cognito sign-in.
 *
 * Signing in to Alter is not signing in to Google and this file does not
 * pretend otherwise. The two are separate grants, granted separately, and
 * revoked separately.
 *
 * **Where the token lives.** In a closure, in this tab, and nowhere else. It
 * is never written to any browser store and never sent anywhere but Google.
 * A token that outlived the tab would be a bearer credential for a year of
 * somebody's life, readable by any script that ever ran on this origin; the
 * price of not keeping it is one button press after a reload, and Google
 * usually answers that without showing a dialog because the consent is
 * already on record.
 *
 * **When it runs out.** Google's access tokens last about an hour. The store
 * calls a token gone a minute before Google does, so a request that starts
 * just inside the window does not arrive just outside it. A call that gets a
 * 401 anyway is handled the same way: ask again silently, and if that fails,
 * say the connection has expired rather than showing an empty calendar.
 *
 * **The client id is never in this file.** It comes from the same config.json
 * the Cognito pool ids come from, written at deploy time. Without one, the
 * screen says so plainly — a Google client of type *Web application* has to
 * exist first, with this site's origin registered on it.
 */

/** Read-only, deliberately. A page that cannot write is better than a page
 * that promises not to: the grant itself is what makes it true. */
export const SCOPE = 'https://www.googleapis.com/auth/calendar.readonly';

export const NOT_CONFIGURED =
  'Google Calendar is not set up for this site yet. It needs a Google client id.';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

/** Treat a token as gone this long before Google does. */
const SAFETY_MS = 60_000;

export function clientIdFor(config) {
  const id = config?.googleClientId;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}

export function isConfigured(config) {
  return clientIdFor(config) !== null;
}

/**
 * The access token, for as long as this tab is open and no longer.
 *
 * `get` answers only while the token is good; `peek` answers even after it has
 * expired, because revoking an expired token on disconnect is still the polite
 * thing to do and Google accepts it.
 */
export function createTokenStore() {
  let token = null;
  let expiresAt = 0;

  return {
    set(grant, now = Date.now()) {
      const seconds = Number(grant?.expires_in);
      token = typeof grant?.access_token === 'string' && grant.access_token ? grant.access_token : null;
      // A grant with no stated lifetime is treated as already gone: asking
      // again costs a round trip, holding a dead token costs a wrong screen.
      expiresAt = token && Number.isFinite(seconds) && seconds > 0 ? now + seconds * 1000 : 0;
      return token;
    },
    get(now = Date.now()) {
      if (!token) return null;
      return now < expiresAt - SAFETY_MS ? token : null;
    },
    peek() {
      return token;
    },
    clear() {
      token = null;
      expiresAt = 0;
    },
  };
}

/**
 * Google Identity Services, loaded once.
 *
 * Not tested: it is a script tag and a load event, and a double narrower than
 * the real one would only prove that the double works.
 */
export function loadGis(doc = document, win = window) {
  if (win.google?.accounts?.oauth2) return Promise.resolve(win.google);

  const existing = doc.querySelector(`script[src="${GIS_SRC}"]`);
  if (existing?.dataset.loading === 'done') return Promise.resolve(win.google);

  return new Promise((resolve, reject) => {
    const script = existing ?? doc.createElement('script');
    script.addEventListener('load', () => {
      script.dataset.loading = 'done';
      if (win.google?.accounts?.oauth2) resolve(win.google);
      else reject(new Error('Google sign-in loaded but is not usable.'));
    });
    script.addEventListener('error', () => reject(new Error('Could not load Google sign-in.')));
    if (!existing) {
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      doc.head.append(script);
    }
  });
}

/**
 * One token client, asked for a token as many times as the tab needs.
 *
 * `prompt: ''` is the silent ask: once consent is on record Google answers
 * without a dialog, which is what makes an expiry mid-session invisible. The
 * first connect passes `prompt: 'consent'` so the student sees exactly what
 * they are granting, and sees that it is read-only.
 */
export function createConsent(clientId, google) {
  let settle = null;

  const client = google.accounts.oauth2.initTokenClient({
    client_id: clientId,
    scope: SCOPE,
    callback: (response) => {
      const done = settle;
      settle = null;
      if (!done) return;
      if (response?.error) done.reject(new Error(response.error_description || response.error));
      else done.resolve(response);
    },
    error_callback: (error) => {
      const done = settle;
      settle = null;
      done?.reject(new Error(error?.type === 'popup_closed'
        ? 'The Google window was closed before it finished.'
        : 'Google sign-in did not finish.'));
    },
  });

  return {
    request(prompt = '') {
      if (settle) settle.reject(new Error('Superseded by a newer request.'));
      return new Promise((resolve, reject) => {
        settle = { resolve, reject };
        client.requestAccessToken({ prompt });
      });
    },
    revoke(token) {
      if (token) google.accounts.oauth2.revoke(token, () => {});
    },
  };
}
